import React, { useState } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PAYMENT_METHODS } from '@/lib/paymentMethods';

function formatFee(entryFee) {
  if (entryFee == null || !Number.isFinite(Number(entryFee))) return null;
  return Number(entryFee);
}

export function PaymentInstructions({ entryFee, className }) {
  const fee = formatFee(entryFee);

  const copyUrl = async (label, url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${label} link`);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className={className}>
      {fee != null && (
        <p className="mb-3 text-sm">
          Entry fee: <strong>${fee}</strong> per entry.
        </p>
      )}
      <p className="mb-3 text-sm text-muted-foreground">
        Include your entry name in the payment note. Payments are confirmed manually —
        your entry stays unpaid until an admin marks it paid.
      </p>
      <ul className="space-y-3">
        {PAYMENT_METHODS.map((m) => (
          <li
            key={m.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2"
          >
            <span className="min-w-[5.5rem] text-sm font-semibold">{m.label}</span>
            <code className="text-sm">{m.handle}</code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => copyUrl(m.label, m.url)}
              aria-label={`Copy ${m.label} link`}
            >
              <Copy className="h-3.5 w-3.5" />
              Copy
            </Button>
            <Button asChild variant="secondary" size="sm">
              <a href={m.url} target="_blank" rel="noopener noreferrer">
                Open <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PaymentDialog({ entryFee, trigger, open, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = open !== undefined;
  const isOpen = controlled ? open : internalOpen;
  const setOpen = controlled ? onOpenChange : setInternalOpen;

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>How to pay</DialogTitle>
          <DialogDescription>
            Fund your entries with one of the options below.
          </DialogDescription>
        </DialogHeader>
        <PaymentInstructions entryFee={entryFee} />
      </DialogContent>
    </Dialog>
  );
}

export default PaymentInstructions;
